import React, { useState, useRef } from 'react';
import { supabase } from '../../supabaseClient.js';
import Grid from '@mui/material/Grid2';
import { IconButton, Typography, TextField, Stack, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import useAlert from '../../alert/useAlert.js';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UpdatePassword = ({setOptionSelected}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSubmitted, setPasswordSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { setAlert } = useAlert();
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const handleResetPassword = async () => {
    if (savingRef.current) return;
    if(passwordError || confirmPasswordError || !newPassword || !confirmPassword) {
      setAlert('Please enter a valid password', 'error');
      return;
    }
    if(newPassword !== confirmPassword) {
      setAlert('Passwords do not match', 'error');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const { data: { user }, error: sessionError } = await supabase.auth.getUser();
      if (sessionError) throw sessionError;
      if (!user) throw new Error('Your session has expired. Please sign in again.');
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setAlert('Password updated successfully!', 'success');
    } catch (error) {
      setAlert(error.message || 'Could not update your password. Please try again.', 'error');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const validatePassword = (password) => {
    // Regex for at least one letter, one special character, and minimum 6 characters
    const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*[^A-Za-z0-9]).{6,}$/;
    if (!strongPasswordRegex.test(password)) {
      setPasswordError('Password must contain at least 6 characters, one letter, and one special character.');
    } else {
      setPasswordError('');
    }
  };

  const validateConfirmPassword = (password) => {
    // Regex for at least one letter, one special character, and minimum 6 characters
    const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*[^A-Za-z0-9]).{6,}$/;
    if (!strongPasswordRegex.test(password)) {
      setConfirmPasswordError('Password must contain at least 6 characters, one letter, and one special character.');
    } else {
      setConfirmPasswordError('');
    }
  };

  return (
    <div>
    <Grid container spacing={2}>
        <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start'}}>
            <Stack spacing={3} width="100%" direction="column" alignItems="flex-start" justifyContent="center" >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                    <Typography sx={{fontWeight: 'bold'}} noWrap variant="h6" gutterBottom>Reset Password</Typography>
                    <IconButton onClick={() => {setOptionSelected('')}}>
                        <ArrowBackIcon />
                        <Typography variant="h6" sx={{color: "#29C0BB", marginLeft: '5px'}}>Back</Typography>
                    </IconButton>
                </Stack>
            <TextField
              inputProps={{
                  autoComplete: 'new-password',
              }}
              required
              fullWidth
              label="Change password"
              variant="outlined"
              focused
              value={newPassword}
              onChange={(e) => {setNewPassword(e.target.value); validatePassword(e.target.value); }}
              placeholder="Create a password" 
              type={showPassword ? "text" : "password"}
              sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
              error={Boolean((passwordSubmitted && !newPassword) || passwordError)}
              helperText={((passwordSubmitted && !newPassword) || passwordError) ? passwordError || "Enter a password" : ""}
              slotProps={{
              input: {
                  endAdornment: (
                  <InputAdornment position="end">
                  <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                  >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                  </InputAdornment>
                  ),
              },
              }}
            />
            <TextField
              inputProps={{
                  autoComplete: 'new-password',
              }}
              autoComplete='off'
              required
              fullWidth
              label="Confirm password"
              variant="outlined"
              focused
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); validateConfirmPassword(e.target.value);} }
              placeholder="Confirm password" 
              type={showConfirmPassword ? "text" : "password"}
              sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
              error={Boolean((passwordSubmitted && !confirmPassword) || confirmPasswordError)}
              helperText={((passwordSubmitted && !confirmPassword)  || confirmPasswordError) ? confirmPasswordError || "Enter a password" : ""}
              slotProps={{
              input: {
                  endAdornment: (
                  <InputAdornment position="end">
                  <IconButton
                      aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                  >
                      {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                  </InputAdornment>
                  ),
              }
              }}
            />
            <Button disabled={saving} onClick={() => { setPasswordSubmitted(true); handleResetPassword();  }} variant="outlined" sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>{saving ? 'Updating…' : 'Reset Password'}</Typography>
            </Button>
            </Stack>
        </Grid> 
      </Grid>
      </div>
  );
};

export default UpdatePassword;

